import React, { useState } from "react";
import { Trash2, Plus, Upload, ExternalLink } from "lucide-react";
import { api, formatApiError, absUrl } from "../api";

/** Label for form fields. */
export function Label({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block text-[12px] uppercase tracking-[0.22em] text-ink2 mb-2 font-semibold font-body">
      {children}
    </label>
  );
}

export function TextField({ field, value, onChange, textarea }) {
  const id = `f-${field.name}`;
  const shared = "w-full bg-white border border-blush-200 rounded-lg px-3 py-2.5 text-[15px] text-ink focus:outline-none focus:border-ink font-body";
  return (
    <div>
      <Label htmlFor={id}>{field.label}</Label>
      {textarea ? (
        <textarea id={id} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                  className={shared + " resize-y"} data-testid={`input-${field.name}`} />
      ) : (
        <input id={id} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
               className={shared} data-testid={`input-${field.name}`} />
      )}
    </div>
  );
}

export function ImageField({ field, value, onChange }) {
  const id = `f-${field.name}`;
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (ex) { setErr(formatApiError(ex, "Upload failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label htmlFor={id}>{field.label}</Label>
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <input id={id} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
               placeholder="/api/uploads/... or full https URL"
               className="flex-1 bg-white border border-blush-200 rounded-lg px-3 py-2.5 text-[15px] text-ink focus:outline-none focus:border-ink font-body"
               data-testid={`input-${field.name}`} />
        <label className="inline-flex items-center gap-2 text-[13px] bg-white border border-blush-200 rounded-lg px-3 py-2.5 cursor-pointer hover:border-magenta/50 transition-colors">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="sr-only" onChange={onFile} disabled={uploading} data-testid={`upload-${field.name}`} />
        </label>
      </div>
      {err && <p className="text-magenta text-xs mt-2">{err}</p>}
      {value && (
        <div className="mt-3 flex items-center gap-3">
          <img src={absUrl(value)} alt="" className="w-24 h-24 object-cover rounded-lg border border-blush-200" />
          <a href={absUrl(value)} target="_blank" rel="noopener noreferrer" className="text-[12px] sc-link inline-flex items-center gap-1">
            Open <ExternalLink size={11} />
          </a>
          <button type="button" onClick={() => onChange("")} className="text-[12px] text-muteink hover:text-magenta inline-flex items-center gap-1">
            <Trash2 size={12} /> Clear
          </button>
        </div>
      )}
    </div>
  );
}

export function StringListField({ field, value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const shared = "flex-1 bg-white border border-blush-200 rounded-lg px-3 py-2.5 text-[15px] text-ink focus:outline-none focus:border-ink font-body";
  return (
    <div>
      <Label>{field.label}</Label>
      <div className="space-y-2">
        {list.map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            {field.multiline ? (
              <textarea rows={3} value={v} onChange={(e) => { const c = [...list]; c[i] = e.target.value; onChange(c); }}
                        className={shared + " resize-y"} data-testid={`input-${field.name}-${i}`} />
            ) : (
              <input type="text" value={v} onChange={(e) => { const c = [...list]; c[i] = e.target.value; onChange(c); }}
                     className={shared} data-testid={`input-${field.name}-${i}`} />
            )}
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))}
                    className="text-muteink hover:text-magenta p-2" title="Remove"
                    data-testid={`remove-${field.name}-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...list, ""])}
              className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-magenta hover:opacity-80"
              data-testid={`add-${field.name}`}>
        <Plus size={14} /> Add item
      </button>
    </div>
  );
}

/**
 * ObjectListField renders a repeatable list of objects. It renders sub-fields
 * via the dynamic `renderField` prop instead of an in-file JSX reference so
 * react-refresh does not choke on mutual recursion.
 */
export function ObjectListField({ field, value, onChange, renderField }) {
  const list = Array.isArray(value) ? value : [];
  const blank = () => Object.fromEntries(field.schema.map((s) => [s.name, ""]));
  return (
    <div>
      <Label>{field.label}</Label>
      <div className="space-y-4">
        {list.map((item, i) => (
          <div key={i} className="border border-blush-200 rounded-xl p-4 bg-blush-50/50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold">
                Item {i + 1}
              </span>
              <div className="flex gap-1">
                <button type="button" disabled={i === 0} onClick={() => {
                  const c = [...list]; [c[i - 1], c[i]] = [c[i], c[i - 1]]; onChange(c);
                }} className="text-muteink hover:text-ink p-1 disabled:opacity-30" title="Move up"
                        data-testid={`up-${field.name}-${i}`}>↑</button>
                <button type="button" disabled={i === list.length - 1} onClick={() => {
                  const c = [...list]; [c[i + 1], c[i]] = [c[i], c[i + 1]]; onChange(c);
                }} className="text-muteink hover:text-ink p-1 disabled:opacity-30" title="Move down"
                        data-testid={`down-${field.name}-${i}`}>↓</button>
                <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))}
                        className="text-muteink hover:text-magenta p-1" title="Remove"
                        data-testid={`remove-${field.name}-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {field.schema.map((sub) =>
                renderField({
                  key: sub.name,
                  field: sub,
                  value: item[sub.name],
                  onChange: (v) => {
                    const c = [...list];
                    c[i] = { ...c[i], [sub.name]: v };
                    onChange(c);
                  },
                })
              )}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...list, blank()])}
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-magenta hover:opacity-80"
              data-testid={`add-${field.name}`}>
        <Plus size={14} /> Add item
      </button>
    </div>
  );
}

/** Central dispatcher — no in-file mutual JSX recursion. */
export function renderField({ field, value, onChange, key }) {
  const commonProps = { field, value, onChange };
  switch (field.type) {
    case "text":       return <TextField key={key} {...commonProps} />;
    case "textarea":   return <TextField key={key} {...commonProps} textarea />;
    case "image":      return <ImageField key={key} {...commonProps} />;
    case "string_list":return <StringListField key={key} {...commonProps} />;
    case "object_list":return <ObjectListField key={key} {...commonProps} renderField={renderField} />;
    default: return null;
  }
}
